import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, ButtonGroup, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChevronLeft, ChevronRight, Plus, Filter, Link as LinkIcon, Check } from 'lucide-react';
import api from '../services/api';
import DayView from './DayView';
import WeekView from './WeekView';
import { Appointment } from './AppointmentCard';
import AppointmentModal from './AppointmentModal';
import AdminBookingWizard from './wizard/AdminBookingWizard';
import { useClipboard } from '../hooks/useClipboard';
import { formatLocalDate } from '../utils/formatters';
import './SchedulingCalendar.css';

interface Professional {
    id: number;
    name: string;
    photo?: string;
    bio?: string;
    agendas?: any[];
}

interface SchedulingCalendarProps {
    professionalId?: number; // For preview/specific view
    readOnly?: boolean;
    height?: string;
}

const SchedulingCalendar: React.FC<SchedulingCalendarProps> = ({
    professionalId: propProfessionalId,
    readOnly = false,
    height
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const prefillClientId = searchParams.get('client') ? parseInt(searchParams.get('client')!) : undefined;
    const [view, setView] = useState<'day' | 'week'>('day');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [professionals, setProfessionals] = useState<Professional[]>([]);
    const [filteredProfessionalId, setFilteredProfessionalId] = useState<number | undefined>(propProfessionalId);
    const [loading, setLoading] = useState(false);
    const [holidays, setHolidays] = useState<any[]>([]);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [appointmentIdToEdit, setAppointmentIdToEdit] = useState<number | undefined>(undefined);
    const [modalInitialDate, setModalInitialDate] = useState<Date>(new Date());
    const [modalInitialTime, setModalInitialTime] = useState<string>('09:00');
    const [modalInitialProfessionalId, setModalInitialProfessionalId] = useState<number | undefined>(undefined);

    // Wizard State
    const [showWizard, setShowWizard] = useState(false);
    const [wizardData, setWizardData] = useState<{
        professionalId: number | null;
        date: Date | null;
        time: string | null;
    }>({
        professionalId: null,
        date: null,
        time: null
    });

    // Clipboard for public link
    const { copied, copyToClipboard } = useClipboard();
    const publicScheduleUrl = `${window.location.origin}/public/schedule`;

    // Initial load
    useEffect(() => {
        fetchProfessionals();
        fetchHolidays();
    }, []);

    useEffect(() => {
        // If prop changes, update filter
        if (propProfessionalId) {
            setFilteredProfessionalId(propProfessionalId);
        }
    }, [propProfessionalId]);

    // Fetch appointments when date, view or filter changes
    useEffect(() => {
        fetchAppointments();
    }, [selectedDate, view, filteredProfessionalId]);

    const fetchHolidays = async () => {
        try {
            const response = await api.get('/holidays/');
            setHolidays(response.data);
        } catch (error) {
            console.error('Error fetching holidays:', error);
        }
    };

    const fetchProfessionals = async () => {
        try {
            // Only fetch professionals with agendas configured
            const response = await api.get('/professionals/with_agendas/');
            setProfessionals(response.data);
        } catch (error) {
            console.error('Error fetching professionals:', error);
        }
    };

    const fetchAppointments = async () => {
        setLoading(true);
        try {
            let start = new Date(selectedDate);
            let end = new Date(selectedDate);

            if (view === 'day') {
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
            } else {
                // Determine week start (Sunday) and end (Saturday)
                const day = start.getDay();
                const diff = start.getDate() - day; // Adjust to Sunday
                start.setDate(diff);
                start.setHours(0, 0, 0, 0);

                end = new Date(start);
                end.setDate(end.getDate() + 6);
                end.setHours(23, 59, 59, 999);
            }

            // Format as YYYY-MM-DD using local time to avoid timezone shift
            const formatLocal = (d: Date) => {
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const params: any = {
                start_date: formatLocal(start),
                end_date: formatLocal(end)
            };

            if (filteredProfessionalId) {
                params.professional = filteredProfessionalId;
            }

            const response = await api.get('/appointments/by_date_range/', { params });
            setAppointments(response.data);
        } catch (error) {
            console.error('Error fetching appointments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePrevious = () => {
        const newDate = new Date(selectedDate);
        if (view === 'day') {
            newDate.setDate(newDate.getDate() - 1);
        } else {
            newDate.setDate(newDate.getDate() - 7);
        }
        setSelectedDate(newDate);
    };

    const handleNext = () => {
        const newDate = new Date(selectedDate);
        if (view === 'day') {
            newDate.setDate(newDate.getDate() + 1);
        } else {
            newDate.setDate(newDate.getDate() + 7);
        }
        setSelectedDate(newDate);
    };

    const handleToday = () => {
        setSelectedDate(new Date());
    };

    const handleTimeSlotClick = (time: Date, professionalId?: number) => {
        if (readOnly) return;

        // Open wizard for new appointment
        const profId = professionalId || filteredProfessionalId;
        if (profId) {
            setWizardData({
                professionalId: profId,
                date: time,
                time: time.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
            });
            setShowWizard(true);
        } else {
            // Fallback to modal if no professional selected
            setModalInitialDate(time);
            setModalInitialTime(time.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
            setModalInitialProfessionalId(undefined);
            setAppointmentIdToEdit(undefined);
            setShowModal(true);
        }
    };

    const handleAppointmentClick = (appointment: Appointment) => {
        if (readOnly) return;

        setModalInitialDate(new Date(appointment.start_time));
        setAppointmentIdToEdit(appointment.id);
        setShowModal(true);
    };

    // formatDateRange logic moved to render for simpler toggle between day/week formats


    // Filter visible professionals correctly
    const visibleProfessionals = filteredProfessionalId
        ? professionals.filter(p => p.id === Number(filteredProfessionalId))
        : professionals;

    // Helper to get slot duration/interval for the calendar
    // In a multi-professional view, we need a standard.
    // If one professional is selected, use their agenda configuration.
    // If multiple, default to 30 or the first one.
    // Calculate dynamic calendar configuration based on visible professionals' agendas
    const calculateCalendarConfig = () => {
        let minStart = 24;
        let maxEnd = 0;
        let minInterval = 9999;
        let hasAgenda = false;

        visibleProfessionals.forEach(prof => {
            if (prof.agendas && prof.agendas.length > 0) {
                // Find agenda that covers the selected date. Use local date to avoid timezone shifts.
                const y = selectedDate.getFullYear();
                const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
                const d = String(selectedDate.getDate()).padStart(2, '0');
                const currentISO = `${y}-${m}-${d}`;

                const agenda = prof.agendas.find((a: any) => {
                    return (!a.start_date || a.start_date <= currentISO) &&
                        (!a.end_date || a.end_date >= currentISO);
                });

                if (agenda) {
                    hasAgenda = true;

                    // Parse Start Time
                    if (agenda.start_time) {
                        const [h] = agenda.start_time.split(':').map(Number);
                        if (!isNaN(h) && h < minStart) minStart = h;
                    }

                    // Parse End Time
                    if (agenda.end_time) {
                        const [h, m] = agenda.end_time.split(':').map(Number);
                        let endH = h;
                        if (m > 0) endH = h + 1; // Round up to ensure slot covers the end time
                        if (!isNaN(endH) && endH > maxEnd) maxEnd = endH;
                    }

                    // Parse Interval
                    if (agenda.slot_duration) {
                        const parts = agenda.slot_duration.split(':');
                        if (parts.length >= 2) {
                            const h = parseInt(parts[0], 10);
                            const m = parseInt(parts[1], 10);
                            const totalMinutes = h * 60 + m;
                            if (totalMinutes > 0 && totalMinutes < minInterval) {
                                minInterval = totalMinutes;
                            }
                        }
                    }
                }
            }
        });

        // Check appointments to expand range if necessary
        appointments.forEach(app => {
            const start = new Date(app.start_time);
            const end = new Date(app.end_time);

            // For now, checks hours. In WeekView, we have appointments from multiple days.
            // We want to ensure the grid covers ALL appointments currently loaded.

            const startH = start.getHours();
            if (startH < minStart) minStart = startH;

            let endH = end.getHours();
            if (end.getMinutes() > 0) endH += 1;
            if (endH > maxEnd) maxEnd = endH;
        });

        // Default constraints
        return {
            startHour: (hasAgenda || appointments.length > 0) && minStart < 24 ? minStart : 8,
            endHour: (hasAgenda || appointments.length > 0) && maxEnd > 0 ? maxEnd : 19,
            interval: hasAgenda && minInterval !== 9999 ? minInterval : 30
        };
    };

    const { startHour, endHour, interval: intervalMinutes } = calculateCalendarConfig();

    return (
        <div className="scheduling-container bg-white rounded shadow-sm" style={height ? { height } : {}}>
            {/* Header Toolbar */}
            <div className="calendar-header p-3 border-bottom d-flex flex-wrap">
                <div className="calendar-header-nav d-flex align-items-center gap-2 flex-grow-1 flex-shrink-0">
                    <Button variant="outline-secondary" onClick={handleToday} className="fw-medium" size="sm">
                        Hoje
                    </Button>

                    <div className="d-flex align-items-center bg-white border rounded shadow-sm flex-grow-1 flex-md-grow-0">
                        <Button variant="ghost" onClick={handlePrevious} className="px-2 border-0 text-secondary">
                            <ChevronLeft size={18} />
                        </Button>
                        <div className="calendar-date-display px-2 text-dark fw-bold flex-grow-1" style={{ textAlign: 'center' }}>
                            {view === 'day' ? (
                                <input
                                    type="date"
                                    value={formatLocalDate(selectedDate)}
                                    onChange={(e) => {
                                        const d = new Date(e.target.value + 'T12:00:00');
                                        if (!isNaN(d.getTime())) setSelectedDate(d);
                                    }}
                                    className="form-control border-0 p-0 text-center fw-bold shadow-none bg-transparent"
                                    style={{ fontSize: 'inherit', cursor: 'pointer' }}
                                />
                            ) : (
                                (() => {
                                    const d = new Date(selectedDate);
                                    const day = d.getDay();
                                    const diff = d.getDate() - day;
                                    const startOfWeek = new Date(d.setDate(diff));
                                    const endOfWeek = new Date(startOfWeek);
                                    endOfWeek.setDate(endOfWeek.getDate() + 6);
                                    return `${startOfWeek.toLocaleDateString('pt-BR')} - ${endOfWeek.toLocaleDateString('pt-BR')}`;
                                })()
                            )}
                        </div>
                        <Button variant="ghost" onClick={handleNext} className="px-2 border-0 text-secondary">
                            <ChevronRight size={18} />
                        </Button>
                    </div>
                </div>

                <div className="calendar-header-actions d-flex align-items-center gap-2 ">
                    {/* Professional Filter */}
                    {!propProfessionalId && (
                        <div className="calendar-filter-wrapper">
                            <Form.Select
                                value={filteredProfessionalId || ''}
                                onChange={(e) => setFilteredProfessionalId(e.target.value ? Number(e.target.value) : undefined)}
                                size="sm"
                                className="border-secondary text-secondary fw-medium"
                            >
                                <option value="">TODAS AGENDAS</option>
                                {professionals.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </Form.Select>
                        </div>
                    )}

                    {/* View Switcher */}
                    <div className="calendar-view-switcher bg-light rounded p-1 d-flex">
                        <Button
                            variant={view === 'day' ? 'white shadow-sm' : 'light'}
                            className={`border-0 rounded px-2 py-1 ${view === 'day' ? 'fw-bold text-primary' : 'text-muted'}`}
                            onClick={() => setView('day')}
                            style={view === 'day' ? { backgroundColor: 'white' } : {}}
                        >
                            Dia
                        </Button>
                        <Button
                            variant={view === 'week' ? 'white shadow-sm' : 'light'}
                            className={`border-0 rounded px-2 py-1 ${view === 'week' ? 'fw-bold text-primary' : 'text-muted'}`}
                            onClick={() => setView('week')}
                            style={view === 'week' ? { backgroundColor: 'white' } : {}}
                        >
                            Semana
                        </Button>
                    </div>

                    {!readOnly && (
                        <>
                            <OverlayTrigger 
                                placement="bottom" 
                                overlay={
                                    <Tooltip id="copy-link">
                                        {copied ? 'Link copiado!' : 'Copiar link de agendamento público'}
                                    </Tooltip>
                                }
                            >
                                <Button 
                                    variant="outline-primary" 
                                    className="d-flex align-items-center justify-content-center gap-1"
                                    onClick={() => copyToClipboard(publicScheduleUrl)}
                                >
                                    {copied ? <Check size={18} /> : <LinkIcon size={18} />}
                                    {copied ? 'Copiado!' : 'Link Público'}
                                </Button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="bottom" overlay={<Tooltip id="new-app">Novo Agendamento</Tooltip>}>
                                <Button variant="success" className="d-flex align-items-center justify-content-center gap-1" onClick={() => handleTimeSlotClick(new Date())}>
                                    <Plus size={18} />Novo Agendamento
                                </Button>
                            </OverlayTrigger>
                        </>
                    )}
                </div>
            </div>

            {/* Calendar View Area */}
            <div className="calendar-grid-container flex-grow-1 bg-white position-relative">
                {loading && (
                    <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{ zIndex: 50 }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Carregando...</span>
                        </div>
                    </div>
                )}

                {view === 'day' ? (
                    <DayView
                        date={selectedDate}
                        appointments={appointments}
                        professionals={visibleProfessionals}
                        holidays={holidays}
                        onTimeSlotClick={handleTimeSlotClick}
                        onAppointmentClick={handleAppointmentClick}
                        interval={intervalMinutes}
                        startHour={startHour}
                        endHour={endHour}
                    />
                ) : (
                    <WeekView
                        date={selectedDate}
                        appointments={appointments}
                        professionals={visibleProfessionals}
                        holidays={holidays}
                        onTimeSlotClick={handleTimeSlotClick}
                        onAppointmentClick={handleAppointmentClick}
                        interval={intervalMinutes}
                        startHour={startHour}
                        endHour={endHour}
                    />
                )}
            </div>

            {/* Wizard for New Appointment */}
            {showWizard && wizardData.professionalId && wizardData.date && wizardData.time && (
                <div className="modal-overlay" onClick={() => setShowWizard(false)}>
                    <div className="modal-content wizard-modal" onClick={e => e.stopPropagation()}>
                        <AdminBookingWizard
                            professionalId={wizardData.professionalId}
                            date={wizardData.date}
                            time={wizardData.time}
                            onSuccess={() => {
                                setShowWizard(false);
                                fetchAppointments();
                            }}
                            onCancel={() => setShowWizard(false)}
                        />
                    </div>
                </div>
            )}

            {/* Modal for Edit Appointment */}
            <AppointmentModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSuccess={() => {
                    fetchAppointments();
                }}
                initialDate={modalInitialDate}
                initialTime={modalInitialTime}
                initialProfessionalId={modalInitialProfessionalId}
                initialClientId={prefillClientId}
                appointmentId={appointmentIdToEdit}
            />
        </div>
    );
};

export default SchedulingCalendar;
